import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * Transformation Manager — one of the five open roles the Careers page lists.
 *
 * Same shape as the other role pages: the mission, the success measures as a
 * list, the responsibilities and the offer as full-width rows, and every
 * "Apply now" pointing at `#contact` — the shared contact band's anchor at the
 * foot of the document — so the button scrolls to the form instead of leaving
 * the page.
 */
export const route: RouteDef = {
  id: 'careersTransformationManager',
  path: 'become-a-digitopian/transformation-manager',
  priority: 0.6,
}

export const careersTransformationManager: WrittenPage = {
  navLabel: 'Transformation Manager',
  title: 'Transformation Manager',
  description:
    'Open role at Digitopia: Transformation Manager, hybrid in Istanbul. Turn roadmaps into a mobilized portfolio with clear ownership, disciplined governance and visible business value.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Careers',
      headline: 'Transformation Manager',
      sub: 'Location: Hybrid (Istanbul) — Full-time',
      body: [
        'An execution leader who connects strategy, people, initiatives and business value — and uses the Impact Platform to keep priorities, dependencies, risks, decisions and outcomes visible in one place.',
      ],
      ctas: [{ label: 'Apply now', href: '#apply' }],
    },
    {
      kind: 'prose',
      heading: 'We’re on a mission',
      paragraphs: [
        'Digitopia is reinventing consulting. We help ambitious organizations #ScaleValue. As creators of the Impact Platform and maturity frameworks, we enable companies to understand where they stand, decide what matters next, and turn digital and AI ambition into measurable progress.',
        'A strong strategy creates direction. Transformation management creates momentum: clear ownership, disciplined governance, visible progress, and the ability to adapt without losing the outcome.',
        'To help organizations move from roadmap to results, we are looking for a Transformation Manager: an execution leader who can connect strategy, people, initiatives and business value. You will help clients establish the operating rhythm of transformation and use Digitopia’s Impact Platform to keep priorities, dependencies, risks, decisions and outcomes visible in one place.',
      ],
    },
    {
      kind: 'list',
      heading: 'What success looks like',
      intro: 'You will make transformation executable and sustainable. Success means:',
      items: [
        'Turning strategic roadmaps into a mobilized portfolio with clear owners, milestones and outcomes.',
        'Creating governance that speeds up decisions and accountability instead of adding bureaucracy.',
        'Making progress, risk, dependencies and business value visible to leadership.',
        'Building the routines and capabilities that allow clients to sustain momentum after the engagement.',
      ],
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What you’ll do',
      items: [
        {
          title: 'Mobilize transformation roadmaps',
          body: 'Translate strategic priorities into initiative charters, owners, workstreams, milestones, resource needs and outcome measures.',
        },
        {
          title: 'Establish the transformation office',
          body: 'Design practical governance, decision rights, meeting cadences, escalation paths and reporting routines.',
        },
        {
          title: 'Manage the portfolio',
          body: 'Track progress across initiatives, surface cross-functional dependencies, resolve blockers, and keep effort focused on the highest-value outcomes.',
        },
        {
          title: 'Measure value',
          body: 'Define and monitor KPIs, benefits, leading indicators and adoption signals so progress is more than activity reporting.',
        },
        {
          title: 'Use the Impact Platform',
          body: 'Configure and embed the platform as the shared system for roadmaps, portfolio visibility, reviews, decisions and continuous improvement.',
        },
        {
          title: 'Enable initiative owners',
          body: 'Coach workstream leaders on planning, risk management, executive communication and outcome ownership.',
        },
        {
          title: 'Support change and adoption',
          body: 'Work with leaders and teams to understand stakeholder impact, address resistance, and build new behaviors into day-to-day operations.',
        },
        {
          title: 'Communicate with clarity',
          body: 'Prepare concise leadership updates that highlight decisions, trade-offs, risks and actions rather than simply reporting status.',
        },
      ],
    },
    {
      kind: 'list',
      heading: 'What makes you a great fit',
      items: [
        'You bring relevant experience in transformation management, program or portfolio management, a Transformation Management Office (TMO), PMO, management consulting or a related field.',
        'You have worked within a complex digital, AI, data, ERP, operating model, customer experience or enterprise-wide transformation program.',
        'You have experience applying transformation management practices such as governance, benefits realization, portfolio management, risk and dependency management, or change management.',
        'You can turn strategic priorities into clear initiatives, ownership, milestones, decisions and measurable outcomes.',
        'You are comfortable using portfolio or transformation management tools, dashboards and data to track progress, risks, dependencies, adoption and business value.',
        'You are highly organized without becoming process-heavy, and understand when governance is supporting progress or slowing it down.',
        'You communicate calmly and clearly with executives, initiative owners and delivery teams.',
        'You are comfortable constructively challenging missed commitments, unclear ownership and weak assumptions.',
        'You understand the human side of transformation and can incorporate stakeholder engagement, change and adoption into execution plans.',
        'You take ownership, anticipate issues, and follow through until the intended outcome is achieved.',
        'You are fluent in English. An additional language is a plus.',
      ],
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What’s in it for you',
      items: [
        {
          title: 'See strategy become real',
          body: 'Work where priorities turn into initiatives, decisions, adoption and measurable business outcomes.',
        },
        {
          title: 'A modern transformation model',
          body: 'Combine hands-on leadership with Digitopia’s frameworks, benchmarks and Impact Platform.',
        },
        {
          title: 'Enterprise-level exposure',
          body: 'Work with senior leaders and cross-functional teams on high-visibility transformation portfolios.',
        },
        {
          title: 'Real ownership',
          body: 'Shape the governance and operating rhythm that determine whether change succeeds.',
        },
        {
          title: 'Continuous growth',
          body: 'Access mentorship, courses, project feedback and internal knowledge-sharing.',
        },
        {
          title: 'Balanced flexibility',
          body: 'Enjoy hybrid working, flexible hours and generous paid time off.',
        },
        {
          title: 'Supportive culture',
          body: 'Join a team that values authenticity, curiosity, bold ideas and learning through action.',
        },
        {
          title: 'Long-term rewards',
          body: 'Receive a highly competitive compensation package with stock options tied to your lasting impact.',
        },
      ],
    },
    {
      kind: 'prose',
      align: 'center',
      heading:
        'Ready to turn ambitious transformation roadmaps into coordinated action and visible business value?',
      sub: 'Apply now. We would love to meet you if you are ready to create momentum, lead through complexity, and own the outcome.',
      cta: { label: 'Apply now', href: '#apply' },
    },
    {
      kind: 'apply',
      opening: 'transformation-manager',
      heading: 'Apply for Transformation Manager',
      sub: 'Hybrid (Istanbul), full-time. Tell us who you are and attach your CV; we read every application and reply either way.',
    },
  ],
}
